import { isUserTokenMember } from '#auth/composables/useCurrentUser'
import { inferOnboardingState, OnboardingScenario } from '#auth/utils/infer'

const onboardingScenarios = [
  OnboardingScenario.OWNER_ACTIVE_NO_PROJECTS,
  OnboardingScenario.INVITED_ACTIVE_NO_PROJECTS,
  OnboardingScenario.INVITED_PENDING_INVITATION,
]
/**
 * Global middleware that prevents users from navigating away from onboarding
 * if they haven't completed the onboarding process yet.
 */
export default defineNuxtRouteMiddleware(async (to) => {
  // Skip during SSR
  if (import.meta.server) {
    return
  }

  const auth = useAuth()

  // Skip middleware for onboarding-related and auth paths. `/auth/*` covers the
  // registration finalize + OAuth callback pages, which run *before* onboarding
  // state is settled (they are what create the project) — same reason /verify-email
  // is skipped. Safe for the old flow, which never visits these routes.
  if (
    to.path.startsWith('/onboarding')
    || to.path.startsWith('/login')
    || to.path.startsWith('/verify-email')
    || to.path.startsWith('/auth/')
    || to.query.auth === 'finalize'
  ) {
    return
  }

  if (!auth.isLoggedIn) {
    return
  }

  // Only apply onboarding guard for token members (not market makers)
  if (!isUserTokenMember(auth.me)) {
    return
  }

  // Check if user needs to complete onboarding
  try {
    const state = inferOnboardingState(auth.me)

    // If user should be redirected to onboarding, redirect them; Advisors won't have an active project anyways
    if (onboardingScenarios.includes(state.scenario) && !auth.isAdvisor) {
      return navigateTo(state.redirectTo)
    }
  }
  catch {
    // If inferOnboardingState throws an error, it means the user state is invalid
    // In this case, redirect to onboarding as a fallback
    return navigateTo(useRuntimeConfig().public.featureFlags.onboarding.path)
  }
})
